import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { intl } from "dmsbasereact";
import { useNavigate } from "react-router-dom";
import { APP_ROUTES } from "../../../utils/NamedRoutes";
import { asyncExportApi } from "../api";

const ExportStartedDialog = ({ open, onClose, jobType, format, filtersArray, hiddenColumns }) => {
    const navigate = useNavigate();

    const [step, setStep] = useState("confirm");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) {
            setStep("confirm");
            setLoading(false);
            setError(null);
        }
    }, [open]);

    const formatLabel = format === "xlsx" ? "Excel (XLSX)" : format === "csv" ? "CSV" : "PDF";

    const handleStart = async () => {
        setLoading(true);
        setError(null);
        try {
            await asyncExportApi.enqueue(jobType, format, filtersArray || [], hiddenColumns || []);
            setStep("started");
        } catch (err) {
            setError(err.message || intl.get("ExportOptions.EnqueueError").d("Failed to start export. Please try again."));
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        onClose();
    };

    const handleGoToDownloads = () => {
        handleClose();
        navigate(APP_ROUTES.MyDownloads);
    };

    if (step === "started") {
        return (
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {intl.get("ExportStarted.Title").d("Your file is being created")}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        {intl
                            .get("ExportStarted.Body")
                            .d("Due to the amount of data, file generation may take a while. We will notify you when it is ready to download.")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {intl
                            .get("ExportStarted.Hint")
                            .d("You can track the progress in My Downloads.")}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="inherit">
                        {intl.get("ExportStarted.Close").d("Close")}
                    </Button>
                    <Button onClick={handleGoToDownloads} variant="contained" startIcon={<DownloadIcon />}>
                        {intl.get("ExportStarted.MyDownloads").d("My Downloads")}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle>
                {intl.get("ExportOptions.Title").d("Export")} — {formatLabel}
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    {intl
                        .get("ExportOptions.Body")
                        .d("The file will be generated in the background and split into parts if needed. You will be notified when it is ready to download.")}
                </Typography>
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="inherit" disabled={loading}>
                    {intl.get("Cancel").d("Cancel")}
                </Button>
                <Button onClick={handleStart} variant="contained" disabled={loading}>
                    {loading
                        ? intl.get("ExportOptions.Starting").d("Starting…")
                        : intl.get("ExportOptions.Confirm").d("Start Export")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

ExportStartedDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    jobType: PropTypes.number.isRequired,
    format: PropTypes.oneOf(["pdf", "xlsx", "csv"]),
    filtersArray: PropTypes.array,
    hiddenColumns: PropTypes.array,
};

ExportStartedDialog.defaultProps = {
    format: "pdf",
    filtersArray: [],
    hiddenColumns: [],
};

export default ExportStartedDialog;
